import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { ShieldAlert, Shield, Lock, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { seedDuressVault, deleteDuressVault, hasDuressVault } from '../utils/duressVault';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * DuressPasswordSetting — Settings → Privacy entry for configuring
 * the duress password + the on-device decoy vault.
 *
 * Flow:
 *   - NOT SET:  shows a "Set up duress password" card with a
 *               two-step wizard (confirm primary, pick duress).
 *   - ENABLED:  shows the current status + Change / Remove actions.
 *
 * Every state-changing action re-authenticates with the primary
 * password so a short-lived session token theft cannot register a
 * new duress backdoor or remove the existing one.
 */
export const DuressPasswordSetting = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [currentPw, setCurrentPw] = useState('');
  const [duressPw, setDuressPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [error, setError] = useState(null);

  const fetchStatus = async () => {
    try {
      const r = await axios.get(`${API}/auth/duress-password-status`);
      setStatus({ ...r.data, hasLocalVault: hasDuressVault() });
    } catch (_e) { /* noop */ }
  };

  useEffect(() => { fetchStatus(); }, []);

  const resetForm = () => {
    setCurrentPw(''); setDuressPw(''); setConfirmPw(''); setError(null);
  };

  const onSetup = async () => {
    setError(null);
    if (duressPw !== confirmPw) {
      setError('Duress passwords do not match');
      return;
    }
    if (duressPw.length < 8) {
      setError('Duress password must be at least 8 characters');
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/duress-password`, {
        current_password: currentPw,
        duress_password: duressPw,
      });
      await seedDuressVault(duressPw, user.id);
      toast.success('Duress password enabled · decoy vault seeded locally');
      setOpen(false);
      resetForm();
      await fetchStatus();
    } catch (e) {
      setError(e.response?.data?.detail || 'Setup failed');
    } finally {
      setBusy(false);
    }
  };

  const onRemove = async () => {
    setError(null);
    setBusy(true);
    try {
      await axios.delete(`${API}/auth/duress-password`, {
        data: { current_password: currentPw },
      });
      deleteDuressVault();
      toast.success('Duress password removed · decoy vault wiped');
      setRemoveOpen(false);
      resetForm();
      await fetchStatus();
    } catch (e) {
      setError(e.response?.data?.detail || 'Remove failed');
    } finally {
      setBusy(false);
    }
  };

  const enabled = status?.enabled;

  return (
    <div className="space-y-2" data-testid="duress-password-setting">
      <label className="text-sm text-slate-400 flex items-center gap-2">
        <ShieldAlert className="w-4 h-4 text-amber-400" />
        Duress password
      </label>
      <div className="p-4 rounded-lg bg-secondary/30 space-y-3">
        {enabled ? (
          <>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-white flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  Duress password enabled
                </p>
                <p className="text-xs text-slate-500">
                  Enabled {status.set_at ? new Date(status.set_at).toLocaleDateString() : ''}
                  {!status.hasLocalVault && (
                    <span className="ml-2 text-amber-300">
                      · no decoy vault on THIS device
                    </span>
                  )}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setOpen(true)}
                  data-testid="duress-password-change-btn"
                >
                  Change
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setRemoveOpen(true)}
                  data-testid="duress-password-remove-btn"
                >
                  Remove
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-white">Not configured</p>
            <p className="text-xs text-slate-500 leading-relaxed">
              A second password that, when entered at login, opens a
              plausible-but-fake account with decoy contacts and scripted
              history. Your real account stays cryptographically invisible.
              A silent alert fires server-side so you'll see a warning
              next time you log in with your real password.
            </p>
            <Button
              size="sm"
              onClick={() => setOpen(true)}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="duress-password-setup-btn"
            >
              <Shield className="w-4 h-4 mr-1" />
              Set up duress password
            </Button>
          </>
        )}
      </div>

      {/* Setup / change dialog */}
      <Dialog open={open} onOpenChange={(o) => { if (!o) { setOpen(false); resetForm(); } }}>
        <DialogContent className="bg-card border-border max-w-md" data-testid="duress-password-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldAlert className="w-5 h-5 text-amber-400" />
              {enabled ? 'Change duress password' : 'Set up duress password'}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Pick a password a coercer might reasonably guess OR that you can
              remember under stress. Entering it at login opens the decoy vault.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label htmlFor="current-pw">Confirm primary password</Label>
              <Input
                id="current-pw"
                type="password"
                value={currentPw}
                onChange={(e) => setCurrentPw(e.target.value)}
                className="mt-1"
                autoComplete="current-password"
                data-testid="duress-password-current-input"
              />
            </div>
            <div>
              <Label htmlFor="duress-pw">Duress password</Label>
              <Input
                id="duress-pw"
                type="password"
                value={duressPw}
                onChange={(e) => setDuressPw(e.target.value)}
                className="mt-1"
                autoComplete="new-password"
                data-testid="duress-password-new-input"
              />
            </div>
            <div>
              <Label htmlFor="duress-pw-confirm">Confirm duress password</Label>
              <Input
                id="duress-pw-confirm"
                type="password"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                className="mt-1"
                autoComplete="new-password"
                data-testid="duress-password-confirm-input"
              />
            </div>
            {error && (
              <p className="text-xs text-red-400 flex items-start gap-1">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                {error}
              </p>
            )}
            <div className="text-[11px] text-slate-500 bg-amber-500/5 border border-amber-500/20 rounded p-2 flex gap-2">
              <Lock className="w-3.5 h-3.5 shrink-0 mt-0.5 text-amber-400" />
              <span>
                The duress password is hashed server-side and used to seal a
                decoy vault stored only on this device. Set it up on every
                device you might be coerced on.
              </span>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => { setOpen(false); resetForm(); }}
              disabled={busy}
            >
              Cancel
            </Button>
            <Button
              onClick={onSetup}
              disabled={busy || !currentPw || !duressPw || !confirmPw}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="duress-password-submit-btn"
            >
              {busy ? (
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              ) : (
                <Shield className="w-4 h-4 mr-1" />
              )}
              {enabled ? 'Change' : 'Enable'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove dialog */}
      <Dialog open={removeOpen} onOpenChange={(o) => { if (!o) { setRemoveOpen(false); resetForm(); } }}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle>Remove duress password</DialogTitle>
            <DialogDescription className="text-slate-400">
              Deletes the duress password server-side and wipes the local
              decoy vault. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label htmlFor="remove-current-pw">Confirm primary password</Label>
            <Input
              id="remove-current-pw"
              type="password"
              value={currentPw}
              onChange={(e) => setCurrentPw(e.target.value)}
              autoComplete="current-password"
              data-testid="duress-password-remove-input"
            />
            {error && (
              <p className="text-xs text-red-400">{error}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => { setRemoveOpen(false); resetForm(); }} disabled={busy}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={onRemove} disabled={busy || !currentPw}>
              {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DuressPasswordSetting;
